
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { formatEventType, getSeverityColor } from "@/utils/securityUtils";
import { SecurityEventsTable } from "@/components/admin/security/SecurityEventsTable";
import type { Database } from "@/integrations/supabase/types";

type SecurityEvent = {
  id: string;
  created_at: string;
  event_type: Database["public"]["Enums"]["security_event_type"];
  severity: Database["public"]["Enums"]["security_severity"];
  ip_address: string | null;
  user_agent: string | null;
  event_details: any;
  resolved: boolean;
  resolution_notes: string | null;
  action_taken: string | null;
  user_id: string | null;
  user_email: string | null;
};

type ValidSeverity = 'critical' | 'high' | 'medium' | 'low';
const validSeverities: ValidSeverity[] = ['critical', 'high', 'medium', 'low'];

const isValidSeverity = (severity: string | undefined): severity is ValidSeverity => {
  return !!severity && validSeverities.includes(severity as ValidSeverity);
};

export default function AdminSecurityEvents() {
  const { severity } = useParams<{ severity?: string }>();
  const normalizedSeverity = severity?.toLowerCase();

  const { data: events, isLoading } = useQuery({
    queryKey: ['security-events', normalizedSeverity],
    queryFn: async () => {
      let query = supabase
        .from('security_events')
        .select('*')
        .order('created_at', { ascending: false });

      if (normalizedSeverity && isValidSeverity(normalizedSeverity)) {
        query = query.eq('severity', normalizedSeverity);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SecurityEvent[];
    },
    enabled: !severity || isValidSeverity(normalizedSeverity)
  });

  if (severity && !isValidSeverity(normalizedSeverity)) {
    return (
      <div className="container max-w-7xl mx-auto py-8 px-4">
        <div className="flex flex-col items-center gap-4">
          <p>Invalid severity level</p>
          <Button asChild>
            <Link to="/admin/security/events">Back to Security Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-7xl mx-auto py-8 px-4 space-y-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">
          {severity && isValidSeverity(normalizedSeverity) ? (
            <>
              <span className={getSeverityColor(normalizedSeverity)}>{normalizedSeverity}</span> Security Events
            </>
          ) : (
            "All Security Events"
          )}
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Event History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p>Loading events...</p>
            </div>
          ) : !events || events.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <p>No events found</p>
            </div>
          ) : (
            <SecurityEventsTable 
              events={events} 
              isLoading={isLoading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
